import { Input, InputGroup, InputLeftElement, InputProps, InputRightElement } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { ActionButton } from '../ActionButton'
import { Icon } from '../Icon'
import FilterField, { FILTER_CONTROL_SIZE, IFilterLayoutProps } from './FilterField'

/** Espera padrão da busca das listagens, em ms. */
export const FILTER_SEARCH_DEBOUNCE_MS = 500

/**
 * Teto de largura da busca. Uma caixa de texto não fica melhor com 1200px, e numa barra que só
 * tem busca (as listagens de apoio de Cursos, por exemplo) o campo esticaria a linha inteira.
 */
export const FILTER_SEARCH_MAX_W = '420px'

export interface IFilterSearchProps
  extends Omit<InputProps, 'onChange' | 'value' | 'size' | 'type'>, IFilterLayoutProps {
  /** Valor já confirmado do filtro — o que a página manda para a API. */
  value: string
  /** Recebe o valor **depois** do debounce. A página não monta `setTimeout` próprio. */
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  /** Só mude com um motivo real (uma busca local, que pode responder na hora). */
  delay?: number
}

/**
 * Campo de busca das listagens: `type="search"`, lupa, botão de limpar e **debounce embutido**.
 *
 * O `onChange` só dispara depois de `delay` ms sem digitação, então a página pode buscar direto
 * no efeito dos filtros — sem `setTimeout` remontado listagem a listagem, e sem uma requisição
 * por tecla (o que três listagens faziam antes deste componente).
 */
export default function FilterSearch({
  value,
  onChange,
  label = 'Pesquisar',
  placeholder = 'Buscar...',
  delay = FILTER_SEARCH_DEBOUNCE_MS,
  flex,
  maxW = FILTER_SEARCH_MAX_W,
  minW,
  ...rest
}: IFilterSearchProps) {
  const [draft, setDraft] = useState(value)

  // O `onChange` costuma ser uma arrow inline, com identidade nova a cada render. Guardado em
  // ref, uma renderização do pai não reinicia o timer abaixo — o que, numa tela que renderiza
  // sozinha (um contador, um polling), adiaria a busca indefinidamente.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  // Só reage a mudança vinda de fora (limpar filtros, reset da tela). O eco do próprio debounce
  // chega com o mesmo texto e o React descarta o `setState` sem re-renderizar.
  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (draft === value) return
    const timer = setTimeout(() => onChangeRef.current(draft), delay)
    return () => clearTimeout(timer)
  }, [draft, value, delay])

  const clear = () => {
    setDraft('')
    onChangeRef.current('')
  }

  return (
    <FilterField label={label} flex={flex} maxW={maxW} minW={minW}>
      <InputGroup size={FILTER_CONTROL_SIZE}>
        <InputLeftElement pointerEvents="none" color="gray.400">
          <Icon name="lni lni-search-alt" />
        </InputLeftElement>
        <Input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          // O "x" nativo do `type="search"` limparia o campo sem passar pelo nosso clear,
          // e ficaria lado a lado com o botão abaixo.
          sx={{ '&::-webkit-search-cancel-button': { display: 'none' } }}
          {...rest}
        />
        {draft && (
          <InputRightElement>
            {/* Limpa na hora: esperar o debounce só para apagar tudo parece travamento. */}
            <ActionButton action="cancel" label="Limpar busca" size="xs" mr={0} onClick={clear} />
          </InputRightElement>
        )}
      </InputGroup>
    </FilterField>
  )
}
